import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, ArrowUpRight } from "lucide-react";
import { CATEGORIES, SERVICES, TREATWELL_URL, BRANCHES, IMAGES, formatPrice } from "@/data/salon";
import { useBooking } from "@/context/BookingContext";

export const Services = () => {
  const { openBooking } = useBooking();
  const [active, setActive] = useState("brows");
  const activeCat = CATEGORIES.find((c) => c.id === active);
  const list = SERVICES.filter((s) => s.category === active);
  const branchLabel = (ids) => ids
    ?.map((id) => BRANCHES.find((b) => b.id === id)?.name.replace("Glasgow · ", ""))
    .filter(Boolean)
    .join(" · ");
  const hairCardImage = (service) => {
    if (/(colour|tint|highlight|prelighten)/.test(service.id)) return IMAGES.hair;
    if (/(cut|fringe|restyle)/.test(service.id)) return IMAGES.hairCut;
    if (/(blowdry|shampoo|hair-up|straighten)/.test(service.id)) return IMAGES.hairBlowdry;
    return IMAGES.hairHero;
  };

  return (
    <section id="services" data-testid="services-section" className="py-24 sm:py-32 bg-elevated">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-pink">The Menu</span>
            <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05]">Treatments & rituals</h2>
            <a href={TREATWELL_URL} target="_blank" rel="noopener noreferrer" data-testid="services-treatwell-link" className="mt-4 inline-block font-mono text-[11px] uppercase tracking-[0.15em] text-muted hover:text-pink transition-colors">Prices sourced from Treatwell · view full menu →</a>
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                data-testid={`service-category-${c.id}`}
                onClick={() => setActive(c.id)}
                className={`rounded-full px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] transition-all duration-300 border ${
                  active === c.id ? "bg-ink text-cream border-ink" : "bg-transparent text-ink/70 border-pink/30 hover:border-pink"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          {/* Spotlight image */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="relative h-[52vh] overflow-hidden rounded-3xl border border-pink/25"
              >
                <img src={activeCat.image} alt={activeCat.label} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-pinklight">{activeCat.label}</p>
                  <p className="mt-2 font-serif text-2xl text-cream leading-snug">{activeCat.tagline}</p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Service list */}
          <div className="lg:col-span-7 space-y-3">
            {list.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                whileHover={{ x: 6 }}
                data-testid={`service-card-${s.id}`}
                className="group flex items-center justify-between gap-5 rounded-2xl border border-pink/20 bg-surface p-5 transition-colors hover:border-pink/60 sm:p-6"
              >
                {s.category === "hair" && (
                  <div className="hidden h-20 w-20 shrink-0 overflow-hidden rounded-2xl border border-pink/20 sm:block">
                    <img
                      src={hairCardImage(s)}
                      alt={s.name}
                      loading="lazy"
                      data-testid={`service-image-${s.id}`}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-baseline gap-4">
                    <h3 className="font-serif text-xl sm:text-2xl text-ink">{s.name}</h3>
                    <span className="font-mono text-xs text-muted flex items-center gap-1"><Clock className="h-3 w-3" />{s.duration}</span>
                  </div>
                  <p className="mt-2 text-sm text-muted font-light leading-relaxed max-w-md">{s.desc}</p>
                  {s.branches && (
                    <p data-testid={`service-branches-${s.id}`} className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-pink">
                      {branchLabel(s.branches)} only
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-3 shrink-0">
                  <span className="font-serif text-2xl text-pink">{s.from && <span className="text-xs text-muted mr-1 align-middle">from</span>}£{formatPrice(s.price)}</span>
                  <button
                    data-testid={`service-book-btn-${s.id}`}
                    onClick={() => openBooking(s.id)}
                    className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.15em] text-ink/60 group-hover:text-ink transition-colors"
                  >
                    Book <ArrowUpRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
